import {QueryStatus, useInfiniteQuery, useQuery} from '@tanstack/react-query'
import {
  PoliciesItemsTypes,
  PoliciesListReactQueryResponse,
  PoliciesListResponseType,
  SliceStatus,
} from 'interfaces'
import {PoliciesService} from 'services'

interface UsePoliciesQueryResultType {
  fetchNextPage: () => void
  hasNextPage?: boolean
  status: SliceStatus | QueryStatus
  policies?: PoliciesItemsTypes[]
}

export const usePoliciesQuery = (): UsePoliciesQueryResultType => {
  const {
    data: policiesListResponse,
    fetchNextPage,
    hasNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: ['getPolicies'],
    queryFn: ({pageParam = {}}) =>
      PoliciesService.getPolicies({LastEvaluatedKey: pageParam}),
    getNextPageParam: lastPage => lastPage?.data?.body?.LastEvaluatedKey,
  })

  const policies = policiesListResponse?.pages.flatMap(
    res => res?.data?.body?.Items,
  )

  return {
    fetchNextPage,
    hasNextPage,
    status,
    policies,
  }
}

/**
 * A custom hook to get the list of policies from the react-query cache.
 * It uses `useQuery` with `enabled: false` to read from the cache without making a network request.
 * This is useful for components that need to react to policy list changes without triggering a refetch.
 * @returns {PoliciesItemsTypes[]} A list of policies, or an empty array.
 */
export const usePoliciesList = () => {
  const {data: policiesData} = useQuery<PoliciesListReactQueryResponse>({
    queryKey: ['getPolicies'],
    // `enabled: false` ensures this query will not refetch data, only read from the cache.
    enabled: false,
  })

  const policies = policiesData?.pages?.flatMap(
    (res: PoliciesListResponseType) => res?.data?.body?.Items,
  )

  return policies || []
}
